import { useEffect, useState, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Upload, Trash2, ArrowUp, ArrowDown, ImagePlus, Star } from 'lucide-react';

const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024; // 5MB

const blankImg = (url) => ({
  url,
  caption: '',
  tags: [],
  order: 1,
  is_cover: false,
});

export default function ProductImagesTab({ productId, initialImages, onSaved }) {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Buffer de edición por fila para el input de tags. Permite que el
  // usuario escriba comas libremente; los chips se crean al hacer Enter
  // o al perder foco (onBlur). Llave: índice del array `images`.
  const [tagDrafts, setTagDrafts] = useState({});
  const fileRef = useRef(null);

  useEffect(() => {
    const ordered = (initialImages || []).slice().sort((a, b) => (a.order || 99) - (b.order || 99));
    setImages(
      ordered.map((img, i) => ({
        url: img.url || '',
        caption: img.caption || '',
        tags: Array.isArray(img.tags) ? img.tags : [],
        order: img.order || i + 1,
        is_cover: !!img.is_cover,
      })),
    );
  }, [initialImages]);

  const reorder = (arr) =>
    arr.map((img, i) => ({ ...img, order: i + 1 }));

  const handleUpload = async (file) => {
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      toast.error('Solo JPG, PNG o WEBP');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Imagen muy grande (máx 5MB)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/upload/image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newImg = blankImg(r.data?.url);
      // Si es la primera imagen, marcarla como cover por default
      const next = images.length === 0
        ? [{ ...newImg, is_cover: true }]
        : [...images, newImg];
      setImages(reorder(next));
      toast.success('Imagen subida');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al subir imagen');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const updateAt = (idx, patch) => {
    setImages((prev) => prev.map((img, i) => (i === idx ? { ...img, ...patch } : img)));
  };

  const updateTagsString = (idx, str) => {
    const tags = String(str || '')
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    updateAt(idx, { tags });
  };

  const getTagsDraft = (idx, img) =>
    idx in tagDrafts ? tagDrafts[idx] : (img.tags || []).join(', ');

  const setTagsDraft = (idx, value) => {
    setTagDrafts((prev) => ({ ...prev, [idx]: value }));
  };

  const commitTagsDraft = (idx) => {
    if (!(idx in tagDrafts)) return;
    updateTagsString(idx, tagDrafts[idx]);
    setTagDrafts((prev) => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  };

  const removeAt = (idx) => {
    const removedWasCover = images[idx]?.is_cover;
    let next = images.filter((_, i) => i !== idx);
    if (removedWasCover && next.length > 0) {
      next = next.map((img, i) => ({ ...img, is_cover: i === 0 }));
    }
    setImages(reorder(next));
  };

  const setCover = (idx) => {
    setImages((prev) => prev.map((img, i) => ({ ...img, is_cover: i === idx })));
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = images.slice();
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    setImages(reorder(next));
  };

  const moveDown = (idx) => {
    if (idx === images.length - 1) return;
    const next = images.slice();
    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
    setImages(reorder(next));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Commitear cualquier draft pendiente del input de tags ANTES de
      // construir el array a guardar. Esto cubre el caso en que el
      // usuario hace click en "Guardar" sin haber salido del input.
      let baseImages = images;
      if (Object.keys(tagDrafts).length > 0) {
        baseImages = images.map((img, i) =>
          i in tagDrafts
            ? {
                ...img,
                tags: String(tagDrafts[i] || '')
                  .split(',')
                  .map((t) => t.trim().toLowerCase())
                  .filter(Boolean),
              }
            : img,
        );
        setImages(baseImages);
        setTagDrafts({});
      }
      // Garantizar exactamente UNA cover si hay imágenes.
      let toSave = reorder(baseImages);
      if (toSave.length > 0 && !toSave.some((i) => i.is_cover)) {
        toSave = toSave.map((i, idx) => ({ ...i, is_cover: idx === 0 }));
      }
      const covers = toSave.filter((i) => i.is_cover);
      if (covers.length > 1) {
        const firstCoverIdx = toSave.findIndex((i) => i.is_cover);
        toSave = toSave.map((i, idx) => ({ ...i, is_cover: idx === firstCoverIdx }));
      }
      await api.put(`/products/${productId}`, { images: toSave });
      toast.success('Imágenes guardadas');
      setImages(toSave);
      onSaved?.(toSave);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="product-images-tab">
      <Card className="bg-card/50 backdrop-blur-md border-white/5">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-sm text-white flex items-center gap-2">
              <ImagePlus className="w-4 h-4 text-amber-400" />
              Imágenes del Producto
            </CardTitle>
            <CardDescription className="text-xs text-muted-foreground">
              JPG, PNG o WEBP — máx 5MB. El agente IA puede enviar estas imágenes por WhatsApp usando los tags.
            </CardDescription>
          </div>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPTED.join(',')}
              hidden
              onChange={(e) => handleUpload(e.target.files?.[0])}
              data-testid="product-images-file-input"
            />
            <Button
              size="sm"
              className="text-xs gap-1"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              data-testid="product-images-upload-btn"
            >
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              Subir imagen
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {images.length === 0 ? (
            <div className="rounded-lg border border-dashed border-white/10 p-8 text-center">
              <p className="text-xs text-muted-foreground">
                Aún no hay imágenes. Sube la primera para que el agente pueda compartirla con leads.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {images.map((img, idx) => (
                <div
                  key={`${img.url}-${idx}`}
                  className="grid grid-cols-12 gap-3 p-3 rounded-lg bg-black/20 border border-white/5"
                  data-testid={`product-image-row-${idx}`}
                >
                  <div className="col-span-12 sm:col-span-3">
                    <img
                      src={img.url}
                      alt={img.caption || `imagen-${idx}`}
                      className="w-full h-28 object-cover rounded-md border border-white/10"
                    />
                  </div>
                  <div className="col-span-12 sm:col-span-9 space-y-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Caption</Label>
                      <Input
                        value={img.caption}
                        onChange={(e) => updateAt(idx, { caption: e.target.value })}
                        placeholder='Ej: "Vista de la cocina con isla central"'
                        className="bg-black/20 border-white/10 text-xs mt-1"
                        data-testid={`product-image-caption-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        Tags (separados por coma)
                      </Label>
                      <Input
                        value={getTagsDraft(idx, img)}
                        onChange={(e) => setTagsDraft(idx, e.target.value)}
                        onBlur={() => commitTagsDraft(idx)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            commitTagsDraft(idx);
                          }
                        }}
                        placeholder="cocina, interior, acabados"
                        className="bg-black/20 border-white/10 text-xs mt-1"
                        data-testid={`product-image-tags-${idx}`}
                      />
                      {img.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {img.tags.map((t) => (
                            <span
                              key={t}
                              className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <label className="flex items-center gap-2 cursor-pointer text-xs text-white">
                        <input
                          type="radio"
                          name="cover-radio"
                          checked={img.is_cover}
                          onChange={() => setCover(idx)}
                          className="accent-amber-400"
                          data-testid={`product-image-cover-${idx}`}
                        />
                        <Star className={`w-3.5 h-3.5 ${img.is_cover ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground'}`} />
                        Imagen de portada
                      </label>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          disabled={idx === 0}
                          onClick={() => moveUp(idx)}
                          data-testid={`product-image-up-${idx}`}
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          disabled={idx === images.length - 1}
                          onClick={() => moveDown(idx)}
                          data-testid={`product-image-down-${idx}`}
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-red-400 hover:text-red-300"
                          onClick={() => removeAt(idx)}
                          data-testid={`product-image-delete-${idx}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-500 text-white text-xs gap-2"
              data-testid="product-images-save-btn"
            >
              {saving && <Loader2 className="w-3 h-3 animate-spin" />}
              Guardar imágenes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
